import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

function AdminPanel() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  
  // Optimized sync loading states
  const [optimizedSyncLoading, setOptimizedSyncLoading] = useState(false);
  const [smartSyncLoading, setSmartSyncLoading] = useState(false);
  const [fullSyncLoading, setFullSyncLoading] = useState(false);
  const [contractorSyncLoading, setContractorSyncLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncProgress, setSyncProgress] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (decoded.role !== 'admin') {
        localStorage.clear();
        navigate('/login');
      }
    } catch {
      localStorage.clear();
      navigate('/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchUsers();
  }, []);

  // Brand color variables
  const colors = {
    background: 'var(--tc-bg)',
    card: 'var(--tc-card)',
    text: 'var(--tc-text)',
    border: 'var(--tc-border)',
    navy: 'var(--tc-color-navy)',
    purple: 'var(--tc-color-purple)',
    sky: 'var(--tc-color-sky)',
    sky50: 'var(--tc-color-sky-50)',
    green: 'var(--tc-color-green)',
    yellow: 'var(--tc-color-yellow)',
    orange: 'var(--tc-color-orange)',
    pink: 'var(--tc-color-pink)'
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/users', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ username, password, role }),
      });
      if (!res.ok) throw new Error('Failed to create user');
      setUsername('');
      setPassword('');
      setRole('user');
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    setError(null);
    try {
      const res = await fetch(`/api/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete user');
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async (id) => {
    const newPassword = prompt('Enter new password:');
    if (!newPassword) return;
    setError(null);
    try {
      const res = await fetch(`/api/users/${id}/reset-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ newPassword: newPassword }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to reset password');
      }
      alert('Password reset successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  // Optimized sync handlers
  const handleOptimizedSync = async () => {
    setOptimizedSyncLoading(true);
    setMessage('');
    setError('');
    try {
      const res = await fetch('/api/v1/sync/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type: 'full', force: false }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessage(data.message || 'Optimized sync started successfully');
        setSyncStatus('running');
        pollSyncProgress();
      } else {
        const errorData = await res.json();
        setError(errorData.error || 'Failed to start optimized sync');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setOptimizedSyncLoading(false);
    }
  };

  const handleSmartSync = async () => {
    setSmartSyncLoading(true);
    setMessage('');
    setError('');
    try {
      const res = await fetch('/api/v1/sync/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type: 'smart', force: false }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessage(data.message || 'Smart sync started successfully');
        setSyncStatus('running');
        pollSyncProgress();
      } else {
        const errorData = await res.json();
        setError(errorData.error || 'Failed to start smart sync');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSmartSyncLoading(false);
    }
  };

  const handleFullSync = async () => {
    setFullSyncLoading(true);
    setMessage('');
    setError('');
    try {
      const res = await fetch('/api/v1/sync/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type: 'full', force: true }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessage(data.message || 'Full sync started successfully');
        setSyncStatus('running');
        pollSyncProgress();
      } else {
        const errorData = await res.json();
        setError(errorData.error || 'Failed to start full sync');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setFullSyncLoading(false);
    }
  };

  const pollSyncProgress = async () => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/v1/sync/status', {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (res.ok) {
          const data = await res.json();
          setSyncStatus(data.status);
          setSyncProgress(data.progress);
          
          if (data.status === 'completed' || data.status === 'failed') {
            clearInterval(interval);
            setMessage(data.message || `Sync ${data.status}`);
          }
        }
      } catch (err) {
        console.error('Error polling sync status:', err);
      }
    }, 2000);
  };

  const handleStopSync = async () => {
    try {
      const res = await fetch('/api/v1/sync/stop', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessage(data.message || 'Sync stopped successfully');
        setSyncStatus('stopped');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGetSyncStats = async () => {
    try {
      const res = await fetch('/api/v1/sync/stats', {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessage(`Sync Stats: ${JSON.stringify(data, null, 2)}`);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Contractor sync handler
  const handleContractorSync = async () => {
    if (!window.confirm('This will sync all 240+ tutors from TutorCruncher. This may take several minutes. Continue?')) {
      return;
    }
    
    setContractorSyncLoading(true);
    setMessage('');
    setError('');
    try {
      const res = await fetch('/api/contractors/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessage(data.message || 'Tutor sync started successfully');
      } else {
        const errorData = await res.json();
        setError(errorData.error || 'Failed to start tutor sync');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setContractorSyncLoading(false);
    }
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <div className="p-6">
      
      {/* Navigation Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/')}
              className="bg-primary text-white px-4 py-2 rounded-md font-medium hover:bg-primary/90 transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={handleLogout}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-200 transition-colors border border-gray-300"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Admin Controls
          </h2>
          <p className="text-gray-600">
            Manage users and sync data with TutorCruncher
          </p>
        </div>

        {/* User Management */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-5">User Management</h3>
          
          {/* Create User Form */}
          <div style={{ marginBottom: "2rem" }}>
            <h4 style={{ marginBottom: "1rem", color: colors.text, fontSize: '1.1rem', fontWeight: 600 }}>Create New User</h4>
            <form onSubmit={handleCreateUser} style={{
              display: "flex",
              gap: "1rem",
              flexWrap: "wrap",
              alignItems: "center"
            }}>
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                style={{
                  flex: "1 1 120px",
                  padding: "0.6rem 1rem",
                  borderRadius: "6px",
                  border: `1px solid ${colors.border}`,
                  fontSize: "1rem",
                  background: "#f9fafb",
                  color: colors.text,
                  outline: "none",
                  fontWeight: 500,
                  transition: "border 0.2s"
                }}
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={{
                  flex: "1 1 120px",
                  padding: "0.6rem 1rem",
                  borderRadius: "6px",
                  border: `1px solid ${colors.border}`,
                  fontSize: "1rem",
                  background: "#f9fafb",
                  color: colors.text,
                  outline: "none",
                  fontWeight: 500,
                  transition: "border 0.2s"
                }}
              />
              <select
                value={role}
                onChange={e => setRole(e.target.value)}
                style={{
                  flex: "1 1 100px",
                  padding: "0.6rem 1rem",
                  borderRadius: "6px",
                  border: `1px solid ${colors.border}`,
                  fontSize: "1rem",
                  background: "#f9fafb",
                  color: colors.text,
                  fontWeight: 500,
                  outline: "none",
                  cursor: "pointer"
                }}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <button
                type="submit"
                style={{
                  background: colors.navy,
                  color: '#fff',
                  fontWeight: 600,
                  padding: '0.6rem 1.3rem',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  fontSize: '1rem',
                  transition: 'background 0.2s',
                  fontFamily: 'Poppins, sans-serif'
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#1a1b5a')}
                onMouseLeave={e => (e.currentTarget.style.background = colors.navy)}
              >
                Create User
              </button>
            </form>
          </div>

          {/* Current Users */}
          <div style={{ marginBottom: "2rem" }}>
            <h4 style={{ marginBottom: "1rem", color: colors.text, fontSize: '1.1rem', fontWeight: 600 }}>Current Users</h4>
            {loading ? (
              <p style={{ color: colors.text, margin: 0 }}>Loading...</p>
            ) : (
              <ul style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                maxHeight: "300px",
                overflowY: "auto"
              }}>
                {users.map(user => (
                  <li key={user._id} style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0.75rem 0",
                    borderBottom: `1px solid ${colors.border}`
                  }}>
                    <span>
                      <span style={{ fontWeight: 700, color: colors.text }}>{user.username}</span>
                      <span style={{ color: "#718096", fontWeight: 500, marginLeft: 8 }}>
                        ({user.role})
                      </span>
                    </span>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        onClick={() => handleResetPassword(user._id)}
                        style={{
                          background: '#fff',
                          color: colors.navy,
                          border: `1px solid ${colors.navy}`,
                          borderRadius: "6px",
                          padding: "0.3rem 0.9rem",
                          cursor: "pointer",
                          fontSize: "0.95rem",
                          fontWeight: 600,
                          transition: "background 0.2s, color 0.2s"
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = colors.navy;
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = '#fff';
                          e.currentTarget.style.color = colors.navy;
                        }}
                      >
                        Reset Password
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user._id)}
                        style={{
                          background: colors.pink,
                          color: "#fff",
                          border: "none",
                          borderRadius: "6px",
                          padding: "0.3rem 0.9rem",
                          cursor: "pointer",
                          fontSize: "0.95rem",
                          fontWeight: 600,
                          transition: "background 0.2s"
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#a32457')}
                        onMouseLeave={e => (e.currentTarget.style.background = colors.pink)}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Data Sync Controls */}
        <div style={{
          background: colors.card,
          borderRadius: "12px",
          boxShadow: "0 2px 18px #2b6cb015",
          padding: "2rem",
          border: `1px solid ${colors.border}`
        }}>
          <h3 style={{ marginBottom: '20px', color: colors.navy, fontSize: '1.5rem', fontWeight: 700 }}>Data Sync</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
            <button onClick={handleOptimizedSync} style={{
              background: colors.green,
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontWeight: 'bold',
              cursor: 'pointer',
              flexShrink: 0
            }}>
              {optimizedSyncLoading ? "Starting..." : "🚀 Optimized Sync"}
            </button>
            <button onClick={handleSmartSync} style={{
              background: colors.sky,
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontWeight: 'bold',
              cursor: 'pointer',
              flexShrink: 0
            }}>
              {smartSyncLoading ? "Starting..." : "🧠 Smart Sync"}
            </button>
            <button onClick={handleFullSync} style={{
              background: colors.pink,
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontWeight: 'bold',
              cursor: 'pointer',
              flexShrink: 0
            }}>
              {fullSyncLoading ? "Starting..." : "🔄 Full Sync"}
            </button>
            <button onClick={handleStopSync} style={{
              background: colors.purple,
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontWeight: 'bold',
              cursor: 'pointer',
              flexShrink: 0
            }}>
              ⏹️ Stop Sync
            </button>
            <button onClick={handleGetSyncStats} style={{
              background: colors.navy,
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontWeight: 'bold',
              cursor: 'pointer',
              flexShrink: 0
            }}>
              📊 Sync Stats
            </button>
            <button onClick={handleContractorSync} style={{
              background: colors.orange,
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontWeight: 'bold',
              cursor: 'pointer',
              flexShrink: 0
            }}>
              {contractorSyncLoading ? "Starting..." : "👥 Sync All Tutors"}
            </button>
          </div>
          
          {/* Sync Status Display */}
          {syncStatus && (
            <div style={{ 
              padding: '10px', 
              marginBottom: '10px', 
              borderRadius: '6px',
              background: syncStatus === 'running' ? 'rgba(52,178,86,0.12)' : 
                         syncStatus === 'completed' ? 'rgba(80,200,223,0.12)' : 
                         syncStatus === 'failed' ? 'rgba(218,46,114,0.12)' : '#f3f4f6',
              color: syncStatus === 'running' ? '#1f7a3a' : 
                    syncStatus === 'completed' ? '#106a78' : 
                    syncStatus === 'failed' ? '#8f1f47' : '#374151',
              fontWeight: 600
            }}>
              Status: {syncStatus.toUpperCase()}
              {syncProgress && (
                <div style={{ marginTop: '5px' }}>
                  Progress: {syncProgress.current}/{syncProgress.total} ({Math.round((syncProgress.current / syncProgress.total) * 100)}%)
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error and Message Display */}
        {error && (
          <div style={{
            background: colors.pink,
            color: "#fff",
            borderRadius: "8px",
            padding: "1rem 1.2rem",
            fontWeight: 500,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
          }} role="alert">
            {error}
          </div>
        )}
        {message && (
          <div style={{
            background: colors.sky,
            color: "#fff",
            borderRadius: "8px",
            padding: "1rem 1.2rem",
            fontWeight: 500,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
          }} role="status">
            {message}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;